import type { Pergunta } from '../types';

/** Regras do app de coleta que não dependem de tela nem de rede (testáveis sozinhas). */

export type Canal = 'PRESENCIAL' | 'TELEFONE' | 'WHATSAPP' | 'ONLINE';
export const CANAIS: { valor: Canal; rotulo: string }[] = [
  { valor: 'PRESENCIAL', rotulo: 'Presencial' },
  { valor: 'TELEFONE', rotulo: 'Telefone' },
  { valor: 'WHATSAPP', rotulo: 'WhatsApp' },
  { valor: 'ONLINE', rotulo: 'Online' },
];
export const FAIXAS = ['18-24', '25-34', '35-44', '45-59', '60+'] as const;
export const SEXOS: { valor: string; rotulo: string }[] = [
  { valor: 'F', rotulo: 'Feminino' },
  { valor: 'M', rotulo: 'Masculino' },
  { valor: 'OUTRO', rotulo: 'Outro' },
  { valor: 'NAO_INFORMADO', rotulo: 'Prefere não dizer' },
];
export const MAX_TEXTO = 300;

/** O que o pesquisador preenche numa resposta. */
export type Rascunho = {
  canal: Canal | '';
  faixaEtaria: string;
  sexo: string;
  notaAprovacao: number | null;
  respostas: Record<string, unknown>;
};
export const rascunhoVazio = (canal: Canal | '' = ''): Rascunho => ({ canal, faixaEtaria: '', sexo: '', notaAprovacao: null, respostas: {} });

/** Corpo enviado à API (POST /pesquisas/:id/respostas). */
export type RespostaPayload = {
  codIbge: string;
  canal: Canal;
  faixaEtaria?: string;
  sexo?: string;
  notaAprovacao: number;
  respostas: Record<string, unknown>;
  coletadaEm: string;
  idLocal: string;
};

/** Mesmas regras da API, para avisar a pessoa na hora (mesmo sem internet) em vez de a resposta ser recusada só depois. */
export function validarRascunho(perguntas: Pergunta[], r: Rascunho): string[] {
  const erros: string[] = [];
  if (!r.canal) erros.push('Escolha como a pessoa foi entrevistada (presencial, telefone…).');
  if (r.notaAprovacao === null) erros.push('Informe a nota de aprovação, de 0 a 10.');
  for (const p of perguntas) {
    const v = r.respostas[p.id];
    const vazio = v === undefined || v === null || v === '';
    if (vazio) {
      if (p.obrigatoria) erros.push(`Responda: ${p.texto}`);
      continue;
    }
    if (p.tipo === 'texto' && typeof v === 'string' && v.length > MAX_TEXTO) erros.push(`"${p.texto}": no máximo ${MAX_TEXTO} caracteres.`);
  }
  return erros;
}

/** Monta o que vai para a fila. `idLocal` evita duplicar se o envio for repetido; `coletadaEm` guarda a hora real da entrevista. */
export function montarPayload(codIbge: string, r: Rascunho, agora: Date, idLocal: string): RespostaPayload {
  const respostas: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(r.respostas)) if (v !== undefined && v !== null && v !== '') respostas[k] = v;
  return {
    codIbge,
    canal: r.canal as Canal,
    ...(r.faixaEtaria ? { faixaEtaria: r.faixaEtaria } : {}),
    ...(r.sexo ? { sexo: r.sexo } : {}),
    notaAprovacao: r.notaAprovacao as number,
    respostas,
    coletadaEm: agora.toISOString(),
    idLocal,
  };
}

export function novoIdLocal(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * O que fazer com a resposta da API a um envio:
 *  - enviada: gravou (201) ou já existia (200, mesmo idLocal): sai da fila;
 *  - rejeitar: a API disse que ESTA resposta não pode ser aceita (pesquisa encerrada, município retirado, data fora do período…);
 *    repetir não adianta, então fica à vista da pessoa com o motivo;
 *  - sessao: a sessão acabou, entrar de novo e continuar depois;
 *  - tentar_depois: falta de rede, limite de requisições ou erro do servidor: a resposta fica guardada e é tentada de novo.
 */
export type Desfecho = 'enviada' | 'rejeitar' | 'sessao' | 'tentar_depois';
export function desfechoDoEnvio(status: number): Desfecho {
  if (status === 200 || status === 201) return 'enviada';
  if (status === 401) return 'sessao';
  if ([400, 403, 404, 409, 413, 422].includes(status)) return 'rejeitar';
  return 'tentar_depois';
}

/** Quantas respostas deste município (nesta pesquisa) ainda estão só no aparelho; entram na contagem de progresso. */
export function pendentesDoMunicipio(fila: { pesquisaId: string; payload: { codIbge: string }; estado: string }[], pesquisaId: string, codIbge: string): number {
  return fila.filter((i) => i.estado === 'pendente' && i.pesquisaId === pesquisaId && i.payload.codIbge === codIbge).length;
}
